import React, { useEffect, useMemo, useState } from "react";
import {
  ArrowDown,
  ArrowUp,
  Minus,
  TrendingUp,
} from "lucide-react";
import { fetchReport } from "@/lib/api";
import { formatINR } from "@/lib/format";

/**
 * Compare-months view for the Budget tab.
 *
 * Source of truth: the existing /api/report/:id `timeline` array, which is
 * already aggregated month × category. We re-use it instead of adding new
 * endpoints (keeps server.py lean per #5 architectural constraint).
 *
 * Renders a side-by-side panel: pick two months → see total spend delta,
 * top-3 categories, and the % swing for each. Auto-selects "this month"
 * vs "last month" by default.
 */

const monthLabelFromIso = (iso) => {
  const m = String(iso || "").match(/^(\d{4})-(\d{2})$/);
  if (!m) return iso || "—";
  const d = new Date(Number(m[1]), Number(m[2]) - 1, 1);
  return d.toLocaleDateString("en-IN", { month: "short", year: "2-digit" });
};

function pickReportId() {
  // The route is /dashboard/:reportId — pull it directly from the URL so we
  // don't have to thread props through every Dashboard ancestor.
  const m = (typeof window !== "undefined" ? window.location.pathname : "").match(
    /\/dashboard\/([^/]+)/
  );
  return m ? m[1] : null;
}

export default function MonthCompare() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [a, setA] = useState("");
  const [b, setB] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const rid = pickReportId();
      if (!rid) {
        setLoading(false);
        return;
      }
      try {
        const r = await fetchReport(rid);
        if (cancelled) return;
        const tl =
          (r?.reportData?.timeline ||
            r?.timeline ||
            []).slice();
        setData(tl);
        // Default selection: latest two months that have any data.
        const populated = tl.filter((row) => (row.total || 0) > 0);
        if (populated.length >= 2) {
          setA(populated[populated.length - 1].month);
          setB(populated[populated.length - 2].month);
        } else if (populated.length === 1) {
          setA(populated[0].month);
        }
      } catch {
        /* gracefully hide */
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const months = useMemo(() => (data || []).slice().reverse(), [data]);

  const rowA = useMemo(
    () => (data || []).find((r) => r.month === a) || null,
    [data, a]
  );
  const rowB = useMemo(
    () => (data || []).find((r) => r.month === b) || null,
    [data, b]
  );

  if (loading) return null;
  if (!data || data.length === 0) return null;

  return (
    <div className="cartiq-card" data-testid="month-compare-card">
      <div className="flex items-start justify-between gap-3 mb-4 flex-wrap">
        <div>
          <div className="text-[11px] uppercase tracking-[0.22em] text-[#FF6B00] font-bold mb-1">
            — Compare months
          </div>
          <h2 className="font-display uppercase text-2xl flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-[#FF6B00]" /> Side-by-side
          </h2>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 mb-5">
        <MonthSelect
          label="Month A"
          value={a}
          options={months}
          onChange={setA}
          testid="month-compare-a"
        />
        <MonthSelect
          label="Month B"
          value={b}
          options={months}
          onChange={setB}
          testid="month-compare-b"
        />
      </div>

      {(!rowA || !rowB) && (
        <div className="text-xs text-[#888] py-4 text-center">
          Pick two months above to see the swing.
        </div>
      )}

      {rowA && rowB && <CompareTable a={rowA} b={rowB} />}
    </div>
  );
}

const MonthSelect = ({ label, value, options, onChange, testid }) => (
  <label className="block">
    <div className="text-[10px] uppercase tracking-[0.22em] text-[#888] font-bold mb-1.5">
      {label}
    </div>
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="cartiq-input w-full font-mono-num text-sm"
      data-testid={testid}
    >
      <option value="" disabled>
        Choose
      </option>
      {options.map((m) => (
        <option key={m.month} value={m.month}>
          {monthLabelFromIso(m.month)} · {formatINR(m.total || 0)}
        </option>
      ))}
    </select>
  </label>
);

const CompareTable = ({ a, b }) => {
  const totalA = a.total || 0;
  const totalB = b.total || 0;
  const delta = totalA - totalB;
  const pct =
    totalB === 0 ? (totalA === 0 ? 0 : 100) : Math.round((delta / totalB) * 100);

  // Compose union of categories across both months → compute side-by-side.
  const cats = useMemo(() => {
    const map = new Map();
    Object.entries(a.categories || {}).forEach(([k, v]) => map.set(k, { a: v || 0, b: 0 }));
    Object.entries(b.categories || {}).forEach(([k, v]) => {
      const prev = map.get(k) || { a: 0, b: 0 };
      prev.b = v || 0;
      map.set(k, prev);
    });
    return Array.from(map.entries())
      .map(([cat, vals]) => ({ cat, ...vals, sum: vals.a + vals.b }))
      .sort((x, y) => y.sum - x.sum)
      .slice(0, 5);
  }, [a, b]);

  return (
    <div data-testid="month-compare-table" className="space-y-4">
      {/* Total swing hero row */}
      <div className="grid grid-cols-3 gap-2 sm:gap-3 items-center bg-[#14141A] border border-[#2A2A35] px-3 py-3">
        <Side
          label={monthLabelFromIso(a.month)}
          value={formatINR(totalA)}
          align="left"
        />
        <SwingPill delta={delta} pct={pct} />
        <Side
          label={monthLabelFromIso(b.month)}
          value={formatINR(totalB)}
          align="right"
        />
      </div>

      {/* Top-5 categories */}
      <div>
        <div className="text-[10px] uppercase tracking-[0.22em] text-[#888] font-bold mb-2">
          Top categories
        </div>
        {cats.length === 0 ? (
          <div className="text-xs text-[#666] italic">
            No category data for these months.
          </div>
        ) : (
          <ul className="space-y-1.5">
            {cats.map((c) => {
              const d = (c.a || 0) - (c.b || 0);
              return (
                <li
                  key={c.cat}
                  className="grid grid-cols-[1fr_auto] gap-2 items-baseline text-xs"
                  data-testid={`compare-cat-${c.cat.toLowerCase()}`}
                >
                  <span className="text-[#F0EDE8] uppercase tracking-wider truncate">
                    {c.cat}
                  </span>
                  <div className="flex items-center gap-2 font-mono-num text-[#888]">
                    <span className="text-right tabular-nums">
                      {formatINR(c.a || 0)}
                    </span>
                    <DeltaBadge delta={d} />
                    <span className="text-right tabular-nums">
                      {formatINR(c.b || 0)}
                    </span>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
};

const Side = ({ label, value, align }) => (
  <div className={align === "right" ? "text-right" : "text-left"}>
    <div className="text-[10px] uppercase tracking-[0.22em] text-[#888]">{label}</div>
    <div className="font-mono-num text-base sm:text-lg text-[#F0EDE8]">{value}</div>
  </div>
);

const SwingPill = ({ delta, pct }) => {
  const up = delta > 0;
  const flat = delta === 0;
  const color = flat ? "#888" : up ? "#E24B4A" : "#1D9E75";
  const Icon = flat ? Minus : up ? ArrowUp : ArrowDown;
  return (
    <div
      className="flex flex-col items-center justify-center gap-0.5 text-center px-2"
      data-testid="month-compare-swing"
    >
      <div
        className="w-7 h-7 flex items-center justify-center"
        style={{ background: `${color}1F`, border: `1px solid ${color}55` }}
      >
        <Icon className="w-4 h-4" style={{ color }} />
      </div>
      <span
        className="font-mono-num text-[10px] sm:text-xs leading-none mt-1"
        style={{ color }}
      >
        {flat ? "Flat" : `${pct > 0 ? "+" : ""}${pct}%`}
      </span>
    </div>
  );
};

const DeltaBadge = ({ delta }) => {
  if (delta === 0) {
    return <Minus className="w-3 h-3 text-[#666]" />;
  }
  const up = delta > 0;
  const Icon = up ? ArrowUp : ArrowDown;
  return (
    <Icon
      className={`w-3 h-3 ${up ? "text-[#E24B4A]" : "text-[#1D9E75]"}`}
    />
  );
};
